<template>
  <BasePopup ref="popup" title="Change to Annual Subscription">
    <div class="popup__inner">
      <p>
        By changing to annual billing, you'll save
        {{ billing.switchToAnnualSavings.percent }}%. Wise choice!!
      </p>
      <p v-if="billing.billing_type === 'google'">
        We'll charge you the annual price when your current subscription ends.
      </p>
      <p v-else>
        We'll charge you the annual price today, minus a credit for the time
        remaining on your monthly period.
      </p>
      <form class="subscription-form">
        <v-wait for="Subscribing">
          <template v-slot:waiting>
            <span
              class="subscription-form__btn subscription-form__btn--upgrade btns btns--large"
            >
              Processing...
              <button-spinner />
            </span>
          </template>
          <button
            v-wait:disabled="'Subscribing'"
            type="button"
            class="subscription-form__btn subscription-form__btn--upgrade btns btns--large"
            @click="subscribe"
          >
            <span>Change to Annual</span>
          </button>
          <button
            v-wait:disabled="'Subscribing'"
            type="button"
            class="subscription-form__btn btns btns--white btns--large"
            @click="hide"
          >
            <span>Cancel</span>
          </button>
        </v-wait>
        <FormError :errors="validation.errors" :field="billing.annualPlan.id" />
      </form>
    </div>
  </BasePopup>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useWait } from '@/app/composables/useWait'

import { BillingView } from '@/models/billing'
import { ValidationState } from '@/helpers/form'

import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import BasePopup from '@/components/ui/BasePopup.vue'
import {
  PlanChangeFailedError,
  UserCancelledError,
} from '@/services/cordova.purchase.types'

const props = defineProps<{
  billing: BillingView
}>()

const emit = defineEmits(['subscribed'])
const wait = useWait()

const popup = ref<InstanceType<typeof BasePopup> | null>(null)

const validation = ref(new ValidationState())

function show(): void {
  popup.value?.show()
}

function hide(): void {
  popup.value?.hide()
  validation.value.reset()
}

async function subscribe(): Promise<void> {
  try {
    wait.start('Subscribing')
    await props.billing.subscribeTo(props.billing.annualPlan)
    emit('subscribed')
    validation.value.reset()
    hide()
    wait.end('Subscribing')
  } catch (error) {
    wait.end('Subscribing')
    if (error instanceof UserCancelledError) {
      return
    }
    if (error instanceof PlanChangeFailedError) {
      validation.value.showError(props.billing.annualPlan.id, error.message)
    } else {
      validation.value.showErrors(error)
    }
  }
}

defineExpose({
  show,
  hide,
})
</script>
